/* ezcam — reveal on scroll, product rails, category explorer, hero video. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isRTL = document.documentElement.dir === 'rtl';

  /* Reveal: elements with [data-ez-reveal] fade up once when they enter the viewport. */
  function initReveal(root = document) {
    const items = root.querySelectorAll('[data-ez-reveal]:not(.is-in)');
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  }

  /* Rails: arrow buttons scroll one page; progress bar tracks position. In RTL scrollLeft runs negative. */
  function initRails(root = document) {
    root.querySelectorAll('[data-ez-rail]').forEach((rail) => {
      if (rail.dataset.ready) return;
      rail.dataset.ready = '1';
      const track = rail.querySelector('.ez-rail__track');
      const prev = rail.querySelector('[data-ez-prev]');
      const next = rail.querySelector('[data-ez-next]');
      const bar = rail.querySelector('.ez-rail__progress span');
      if (!track) return;

      const max = () => track.scrollWidth - track.clientWidth;
      const pos = () => Math.abs(track.scrollLeft);
      const update = () => {
        const m = max();
        const p = m > 0 ? pos() / m : 0;
        if (prev) prev.disabled = pos() < 4;
        if (next) next.disabled = pos() > m - 4;
        if (bar) {
          const w = Math.max((track.clientWidth / track.scrollWidth) * 100, 12);
          bar.style.width = `${w}%`;
          bar.style.marginInlineStart = `${(100 - w) * p}%`;
        }
      };
      const go = (dir) => {
        const step = track.clientWidth * 0.9 * dir;
        track.scrollBy({ left: isRTL ? -step : step, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      prev && prev.addEventListener('click', () => go(-1));
      next && next.addEventListener('click', () => go(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* Category explorer: accessible tabs with a sliding pill and a staggered panel entrance. */
  function initExplorers(root = document) {
    root.querySelectorAll('[data-ez-explorer]').forEach((ex) => {
      if (ex.dataset.ready) return;
      ex.dataset.ready = '1';
      const tabs = [...ex.querySelectorAll('[role="tab"]')];
      const pill = ex.querySelector('.ez-explorer__pill');
      const list = ex.querySelector('[role="tablist"]');

      const movePill = (tab) => {
        if (!pill || !tab) return;
        pill.style.width = `${tab.offsetWidth}px`;
        pill.style.left = `${tab.offsetLeft}px`;
      };
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on);
          t.tabIndex = on ? 0 : -1;
          const panel = ex.querySelector(`#${t.getAttribute('aria-controls')}`);
          if (!panel) return;
          panel.hidden = !on;
          if (on) {
            panel.classList.remove('is-entering');
            void panel.offsetWidth; // restart the entrance animation
            panel.classList.add('is-entering');
          }
        });
        movePill(tab);
        if (focus) tab.focus();
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      };

      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          const fwd = isRTL ? 'ArrowLeft' : 'ArrowRight';
          const back = isRTL ? 'ArrowRight' : 'ArrowLeft';
          let j = null;
          if (e.key === fwd) j = (i + 1) % tabs.length;
          if (e.key === back) j = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') j = 0;
          if (e.key === 'End') j = tabs.length - 1;
          if (j !== null) { e.preventDefault(); select(tabs[j], true); }
        });
      });
      const current = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
      requestAnimationFrame(() => movePill(current));
      window.addEventListener('resize', () => movePill(tabs.find((t) => t.getAttribute('aria-selected') === 'true')));
      list && list.addEventListener('scroll', () => movePill(tabs.find((t) => t.getAttribute('aria-selected') === 'true')), { passive: true });
    });
  }

  /* Hero video: play only while visible, never under reduced motion. */
  function initHero(root = document) {
    root.querySelectorAll('[data-ez-hero-video]').forEach((v) => {
      if (reduceMotion) { v.removeAttribute('autoplay'); v.pause(); return; }
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(([e]) => (e.isIntersecting ? v.play().catch(() => {}) : v.pause())).observe(v);
    });
  }

  /* Count-up numbers in the hero facts. */
  function initCounters(root = document) {
    root.querySelectorAll('[data-ez-count]').forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const target = parseInt(el.dataset.ezCount, 10);
      if (!target || reduceMotion) return;
      const suffix = el.dataset.ezSuffix || '';
      const start = performance.now();
      const dur = 1600;
      const tick = (t) => {
        const k = Math.min((t - start) / dur, 1);
        const eased = 1 - Math.pow(1 - k, 3);
        el.textContent = Math.round(target * eased).toLocaleString('he-IL') + suffix;
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  const initAll = (root) => {
    initReveal(root);
    initRails(root);
    initExplorers(root);
    initHero(root);
    initCounters(root);
  };

  // Light / dark mode toggle. The initial mode is set by an inline script in theme.liquid.
  const root = document.documentElement;
  const syncToggles = () => {
    const light = root.getAttribute('data-ez-mode') !== 'dark';
    document.querySelectorAll('[data-ez-mode-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(!light));
      btn.setAttribute('aria-label', light ? 'מעבר למצב כהה' : 'מעבר למצב בהיר');
    });
  };
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-ez-mode-toggle]')) return;
    const next = root.getAttribute('data-ez-mode') === 'dark' ? 'light' : 'dark';
    const animate = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (animate) root.classList.add('ez-mode-anim');
    root.setAttribute('data-ez-mode', next);
    try { localStorage.setItem('ez-mode', next); } catch (err) {}
    syncToggles();
    if (animate) setTimeout(() => root.classList.remove('ez-mode-anim'), 400);
  });

  document.addEventListener('DOMContentLoaded', () => { initAll(document); syncToggles(); });
  // Theme editor: re-run when a section is added or changed.
  document.addEventListener('shopify:section:load', (e) => { initAll(e.target); syncToggles(); });
})();
